import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { CheckCircle, Building2, Loader2, Plus, X, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import PublisherRequestModal from './PublisherRequestModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PublisherSelector - Component for creators to select or request a publisher
 * Used in series/novel creation and editing forms
 * 
 * Props:
 * - value: Current publisher_id or null
 * - status: Current publisher_status ('pending', 'approved', 'rejected', or null)
 * - onChange: (publisher_id: string | null) => void
 * - disabled: boolean
 * - className: string
 */
const PublisherSelector = ({ 
  value, 
  status,
  onChange, 
  disabled = false,
  className = ''
}) => {
  const { token } = useAuth();
  const [publishers, setPublishers] = useState([]);
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetchPublishers();
  }, []);

  useEffect(() => {
    // Find selected publisher details when value changes
    if (value && publishers.length > 0) {
      const found = publishers.find(p => p.id === value);
      setSelectedPublisher(found || null);
    } else {
      setSelectedPublisher(null);
    }
  }, [value, publishers]);

  const fetchPublishers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/publishers?limit=100`);
      if (res.ok) {
        const data = await res.json();
        setPublishers(data.publishers || []);
      }
    } catch (err) {
      console.error('Error fetching publishers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (publisher) => {
    onChange(publisher?.id || null);
    setIsOpen(false);
  };

  const handleRemove = () => {
    onChange(null);
    setSelectedPublisher(null);
  };

  const getStatusBadge = () => {
    if (!status) return null;
    
    switch (status) {
      case 'approved':
        return (
          <span className="flex items-center gap-1 text-xs text-green-400 bg-green-950/50 px-2 py-0.5 rounded">
            <CheckCircle className="w-3 h-3" />
            Approved
          </span>
        );
      case 'pending':
        return (
          <span className="flex items-center gap-1 text-xs text-yellow-400 bg-yellow-950/50 px-2 py-0.5 rounded">
            <Clock className="w-3 h-3" />
            Pending Approval
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center gap-1 text-xs text-red-400 bg-red-950/50 px-2 py-0.5 rounded">
            <AlertCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-gray-500 ${className}`}>
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading publishers...
      </div>
    );
  }

  return (
    <div className={className} data-testid="publisher-selector">
      <label className="text-sm text-gray-400 mb-2 block">Publisher (Optional)</label>
      
      {/* Selected Publisher Display */}
      {selectedPublisher ? (
        <div className="flex items-center justify-between p-3 bg-gray-800 border border-gray-700 rounded-lg">
          <div className="flex items-center gap-3">
            {/* Logo */}
            <div className="w-10 h-10 bg-gray-700 rounded flex items-center justify-center overflow-hidden flex-shrink-0">
              {selectedPublisher.logo_url ? (
                <img 
                  src={selectedPublisher.logo_url} 
                  alt={selectedPublisher.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Building2 className="w-5 h-5 text-gray-500" />
              )}
            </div>
            
            {/* Info */}
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-white">{selectedPublisher.name}</span>
                {selectedPublisher.verified && (
                  <CheckCircle className="w-4 h-4 text-purple-500" />
                )}
              </div>
              {getStatusBadge()}
            </div>
          </div>
          
          {!disabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              className="text-gray-400 hover:text-red-400"
              data-testid="remove-publisher-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      ) : (
        /* Publisher Selection Dropdown */
        <div className="relative">
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-left flex items-center justify-between hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="publisher-dropdown-btn"
          >
            <span className="text-gray-400">Independent / Self-published</span>
            <Building2 className="w-4 h-4 text-gray-500" />
          </button>

          {/* Dropdown */}
          {isOpen && (
            <div className="absolute z-50 mt-1 w-full bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-h-64 overflow-y-auto">
              {/* Independent Option */}
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="w-full p-3 text-left hover:bg-gray-800 flex items-center gap-3 border-b border-gray-700"
              >
                <div className="w-8 h-8 bg-gray-800 rounded flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-gray-500" />
                </div>
                <span className="text-gray-300">Independent / Self-published</span>
              </button>

              {/* Publisher List */}
              {publishers.map((publisher) => (
                <button
                  key={publisher.id}
                  type="button"
                  onClick={() => handleSelect(publisher)}
                  className="w-full p-3 text-left hover:bg-gray-800 flex items-center gap-3"
                  data-testid={`publisher-option-${publisher.id}`}
                >
                  <div className="w-8 h-8 bg-gray-800 rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                    {publisher.logo_url ? (
                      <img 
                        src={publisher.logo_url} 
                        alt={publisher.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-sm font-bold text-gray-600">
                        {publisher.name.charAt(0)}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white">{publisher.name}</span>
                      {publisher.verified && (
                        <CheckCircle className="w-3 h-3 text-purple-500" />
                      )}
                    </div>
                  </div>
                </button>
              ))}

              {/* Request New Publisher */}
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setShowRequestModal(true);
                }}
                className="w-full p-3 text-left hover:bg-purple-900/30 flex items-center gap-3 border-t border-gray-700 text-purple-400"
                data-testid="request-new-publisher-btn"
              >
                <div className="w-8 h-8 bg-purple-900/50 rounded flex items-center justify-center">
                  <Plus className="w-4 h-4" />
                </div>
                <span>Request New Publisher</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Info Text */}
      {!selectedPublisher && !disabled && (
        <p className="text-xs text-gray-500 mt-2">
          Select a publisher if this work is published under them. Association requires approval.
        </p>
      )}

      {/* Request Modal */}
      <PublisherRequestModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        onSuccess={fetchPublishers}
      />
    </div>
  );
};

export default PublisherSelector;
